export const isCPF = (text: string): boolean => {
    /*
        Valida se o cpf está no formato XXX.XXX.XXX-XX onde
        X é um dígito que não se repete 11 vezes
    */
    if (text.match(/^(?!(\d)(\1.*){10})\d{3}\.\d{3}\.\d{3}-\d{2}$/) === null) {
        return false
    }

    //Removendo os . e o - da string do cpf
    text = text.replace(/\D/g, '')

    /*
        Daqui pra frente tem a ver com como o CPF é
        gerado, apenas os 9 primeiros números são
        aleatórios, os 2 últimos vão depender dos 
        nove primeiros, seguindo o seguinte cálculo:
        penúltimo número: pegue os 9 primeiros números
            e multiplique eles da seguinte maneira, o
            primeiro dígito é multiplicado por 10, o
            segundo por 9, o terceiro por 8, e assim
            sucessivamente, após isso some todos os
            resultados, multiplique essa soma por 10,
            pra finalizar pegue o resto desse valor
            dividido por 11, ele deve ser igual ao
            penúltimo dígito do seu CPF OU, caso o
            resto seja igual a 10, o penúltimo
            dígito do CPF deve ser 0
        último número: mesma coisa, mas agora a
            multiplicação dos dígitos é diferente,
            o primeiro dígito é multiplicado por 11,
            o segundo por 10, o terceiro por 9, e
            assim sucessivamente, e pegaremos os 10
            primeiros dígitos ao invés de apenas 9
    */

    let soma: number = 0

    for (let i = 1; i < 10; i++) {
        const digito: string = text.slice(i - 1, i)
        const valor: number = parseInt(digito) * (11 - i)
        soma += valor * 10
    }

    const penultimoDigitoDoCPF: string = text.slice(9, 10)
    let resto: number = soma % 11

    if (resto === 10) {
        resto = 0
    }

    if (`${resto}` !== penultimoDigitoDoCPF) {
        return false
    }

    soma = 0

    for (let i = 1; i < 11; i++) {
        const digito: string = text.slice(i - 1, i)
        const valor: number = parseInt(digito) * (12 - i)
        soma += valor * 10
    }

    const ultimoDigitoDoCPF: string = text.slice(10, 11)
    resto = soma % 11

    if (resto === 10) {
        resto = 0
    }

    return ultimoDigitoDoCPF === `${resto}`
}

export const formatCEP = (cep: string): string => {
    return cep.replace(/^(\d{5})(\d{3})$/, "$1-$2");
}

export const isCNPJ = (text: string): boolean => {
    /*
        Novo formato de CNPJ alfanumérico:

        AA.AAA.AAA/AAAA-XX

        Onde:
        - Os 12 primeiros caracteres podem conter:
          letras e números

        - Os 2 últimos caracteres são os
          dígitos verificadores
    */

    const regex =
        /^(?!([A-Z0-9])\1{13})[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/i

    // Valida o formato do CNPJ
    if (!regex.test(text)) {
        return false
    }

    /*
        Remove:
        - .
        - /
        - -

        Exemplo:
        AB.123.CD4/EF56-78

        vira:

        AB123CD4EF5678
    */
    text = text.replace(/[^\da-z]/gi, '').toUpperCase()

    /*
        Separa:

        base     => 12 primeiros caracteres
        digitos  => 2 últimos dígitos verificadores
    */
    const base = text.slice(0, 12)
    const digitos = text.slice(12)

    /*
        Converte caracteres em valores numéricos

        Números:
        0-9 => 0-9

        Letras:
        A-Z => 10-35

        Exemplo:
        A => 10
        B => 11
        Z => 35
    */
    const charToValue = (char: string): number => {
        // Se for número retorna normalmente
        if (/^\d$/.test(char)) {
            return parseInt(char)
        }

        /*
            charCodeAt retorna o código ASCII

            '0' → 48 - 48 = 0
            '1' → 49 - 48 = 1
            'A' → 65 - 48 = 17
            'B' → 66 - 48 = 18
        */
        return char.charCodeAt(0) - 48
    }

    /*
        Função responsável por calcular
        um dígito verificador
    */
    const calcularDigito = (
        numeros: string,
        pesoInicial: number
    ): number => {
        let soma = 0

        /*
            Peso usado na multiplicação

            Primeiro dígito:
            começa em 5

            Segundo dígito:
            começa em 6
        */
        let peso = pesoInicial

        /*
            Percorre todos os caracteres
        */
        for (const char of numeros) {
            /*
                Exemplo:

                A => 10

                10 * peso
            */
            soma += charToValue(char) * peso

            // Decrementa o peso
            peso--

            /*
                Quando o peso chega abaixo de 2
                ele reinicia em 9

                Sequência:
                5 4 3 2 9 8 7 6...
            */
            if (peso < 2) {
                peso = 9
            }
        }

        /*
            Obtém o resto da divisão por 11
        */
        const resto = soma % 11

        /*
            Regra oficial do CNPJ:

            Se resto < 2:
            dígito = 0

            Senão:
            dígito = 11 - resto
        */
        return resto < 2 ? 0 : 11 - resto
    }

    /*
        Calcula o primeiro dígito
        usando os 12 caracteres da base
    */
    const digito1 = calcularDigito(base, 5)

    /*
        Compara o dígito calculado
        com o digitado no CNPJ
    */
    if (digito1 !== parseInt(digitos[0])) {
        return false
    }

    /*
        Calcula o segundo dígito

        Agora usando:
        - os 12 caracteres da base
        - + o primeiro dígito calculado
    */
    const digito2 = calcularDigito(base + digito1, 6)

    /*
        Retorna true se o segundo
        dígito estiver correto
    */
    return digito2 === parseInt(digitos[1])
}

export const isDocument = (text: string): boolean => {
    return isCPF(text) || isCNPJ(text)
}

export const isTelephoneNumber = (text: string): boolean => {
    const digits = text.replace(/\D/g, "");

    const validDDDs = new Set([
        "11", "12", "13", "14", "15", "16", "17", "18", "19",
        "21", "22", "24", "27", "28",
        "31", "32", "33", "34", "35", "37", "38",
        "41", "42", "43", "44", "45", "46",
        "47", "48", "49",
        "51", "53", "54", "55",
        "61", "62", "64", "65", "66", "67",
        "68", "69",
        "71", "73", "74", "75", "77",
        "79",
        "81", "82", "83", "84", "85", "86", "87", "88", "89",
        "91", "92", "93", "94", "95", "96", "97", "98", "99"
    ]);

    if (digits.length !== 11) return false;

    const ddd = digits.substring(0, 2);
    const number = digits.substring(2);

    if (!validDDDs.has(ddd)) return false;
    if (!number.startsWith("9")) return false;

    return true;
};

export const isEmail = (text: string): boolean => {
    return text.match(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) !== null
}

export const isName = (text: string): boolean => {
    return text.match(/^\s*[A-Za-zÀ-Ÿ][A-zÀ-ÿ']+\s+([A-zÀ-ÿ']+\s*)+\s*$/) !== null
}

export const isDataNascimento = (
    text: string,
    setErrorMessage: (msg: string) => void
): boolean => {

    const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
        setErrorMessage('Data de nascimento inválida.');
        return false;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);

    const date = new Date(year, month - 1, day);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        setErrorMessage('Data de nascimento inválida.');
        return false;
    }

    const today = new Date();
    let age = today.getFullYear() - year;

    const hasHadBirthdayThisYear =
        today.getMonth() > (month - 1) ||
        (today.getMonth() === (month - 1) && today.getDate() >= day);

    if (!hasHadBirthdayThisYear) {
        age--;
    }

    console.log(age)

    if (age < -1) {
        setErrorMessage('Data de nascimento inválida.');
        return false;
    }
    if (age < 18) {
        setErrorMessage('Você precisa ter 18 anos ou mais para criar uma conta.');
        return false;
    }
    if (age > 100) {
        setErrorMessage('Data de nascimento inválida.');
        return false;
    }

    return true;
};

export const formatPrice = (value?: number | string): string => {
    if (value == null) return ''

    const numberValue = typeof value === 'string'
        ? Number(value.replace(',', '.'))
        : value

    if (Number.isNaN(numberValue)) return ''

    return numberValue.toFixed(2).replace('.', ',')
}

export const isCreditCardExpirationDate = (text: string): boolean => {
    // Checa se a data é válida
    if (text.match(/^(0[1-9]|1[0-2])\/\d{2}$/) === null) {
        return false
    }

    //Daqui pra frente checa se a data é após a data atual
    const dataAtual = new Date()
    const anoAtual = `${dataAtual.getFullYear()}`.slice(2)
    const mesAtual = dataAtual.getMonth() + 1
    const [mes, ano] = text.split('/')

    if (parseInt(ano) < parseInt(anoAtual)) {
        return false
    }

    if (parseInt(ano) > parseInt(anoAtual)) {
        return true
    }

    return parseInt(mes) >= mesAtual
}

export const isCreditCardCVV = (text: string): boolean => {
    return text.match(/^\d{3,4}$/) !== null
}

// Essas funções abaixo, verificam qual é a bandeira do cartão de crédito e retornam a bandeira para o usuário
export const CardFlagsRegexEnum = {
    CRED: /^5[1-5]\d{14}$|^2(?:2(?:2[1-9]|[3-9]\d)|[3-6]\d\d|7(?:[01]\d|20))\d{12}/,
    VISA: /^4\d{12}(\d{3})?/,
    DINN: /^3(0[0-5]|[68]\d)\d{11}/,
    AMEX: /^3[47]\d{13}/,
    ELO: /^(4011(78|79)|43(1274|8935)|45(1416|7393|763(1|2))|50(4175|6699|67[0-7][0-9]|9000)|50(9[0-9][0-9][0-9])|627780|63(6297|6368)|650(03([^4])|04([0-9])|05(0|1)|05([7-9])|06([0-9])|07([0-9])|08([0-9])|4([0-3][0-9]|8[5-9]|9[0-9])|5([0-9][0-9]|3[0-8])|9([0-6][0-9]|7[0-8])|7([0-2][0-9])|541|700|720|727|901)|65165([2-9])|6516([6-7][0-9])|65500([0-9])|6550([0-5][0-9])|655021|65505([6-7])|6516([8-9][0-9])|65170([0-4]))/,
    HIPE: /^606282|^637095|^637599|^637568/,
} as const;

export type CardFlag = keyof typeof CardFlagsRegexEnum;

export const isCreditCardNumber = (text: string): boolean => {
    const digits = text.replace(/\D/g, '')
    
    return Object.values(CardFlagsRegexEnum).some(regex => regex.test(digits))
}

const CardFlagsPrefixEnum: Record<CardFlag, RegExp> = {
    HIPE: /^(606282|637095|637599|637568)/,
    ELO: /^(4011(78|79)|43(1274|8935)|45(1416|7393|763(1|2))|50(4175|6699|67[0-7][0-9]|9000)|50(9[0-9][0-9][0-9])|627780|63(6297|6368)|650(03([^4])|04([0-9])|05(0|1)|05([7-9])|06([0-9])|07([0-9])|08([0-9])|4([0-3][0-9]|8[5-9]|9[0-9])|5([0-9][0-9]|3[0-8])|9([0-6][0-9]|7[0-8])|7([0-2][0-9])|541|700|720|727|901)|65165([2-9])|6516([6-7][0-9])|65500([0-9])|6550([0-5][0-9])|655021|65505([6-7])|6516([8-9][0-9])|65170([0-4]))/,
    AMEX: /^3[47]/,
    DINN: /^3(0[0-5]|[68])/,
    CRED: /^(5[1-5]|2(2(2[1-9]|[3-9]\d)|[3-6]\d\d|7([01]\d|20)))/,
    VISA: /^4/,
};

export const getCardFlag = (text: string): CardFlag | null => {
    const digits = text.replace(/\D/g, '')
    if (!digits) return null

    for (const flag of Object.keys(CardFlagsPrefixEnum) as CardFlag[]) {
        if (CardFlagsPrefixEnum[flag].test(digits)) {
            return flag
        }
    }
    return null
}

export const isCEP = (cep: string): boolean => {
    cep = cep.replace(/\D/, '')

    /*
        Esse regex verifica duas coisas:
        1-Se o CEP é formado pelo mesmo dígito repetido 8 vezes, como
        por exemplo: 00000000
        2-Se o CEP é formado por 8 dígitos únicos, como por exemplo:
        12345678
        Caso sim o método .test retornará true
    
    */

    return cep.length === 8 && !/((\d)(?=\2{7})|((\d)(?!(.*\4.*|$))){7})/gm.test(cep)
}

export const isEstado = (estado: string): boolean => {
    return ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'].includes(estado.toUpperCase())
}

export const isDigitoBradesco = (
    numero: string,
    digito: string,
    tipo: 'conta' | 'agencia'
): boolean => {
    if (!numero || !digito) return false; // Não aceitar input vazio

    let pesos: number[] = [];
    let soma = 0;
    let digitoCalculado: string;

    if (tipo === 'conta') {
        // Remover zeros à esquerda apenas da conta
        numero = numero.replace(/^0+/, '');
        if (numero.length === 0) return false; // Se após remover zeros ficou vazio
        pesos = [2, 3, 4, 5, 6, 7, 8, 9];
        const numeros = numero.split('').reverse().map(n => parseInt(n, 10));

        for (let i = 0; i < numeros.length; i++) {
            soma += numeros[i] * pesos[i % pesos.length];
        }

        const resto = soma % 11;

        if (resto === 0 || resto === 1) {
            digitoCalculado = '0';
        } else {
            digitoCalculado = String(11 - resto);
        }
    } else if (tipo === 'agencia') {
        //  sempre completar para 4 dígitos
        numero = numero.padStart(4, '0');

        if (!/^\d{4}$/.test(numero)) return false; // Garantir que são só números

        pesos = [5, 4, 3, 2];
        const numeros = numero.split('').map(n => parseInt(n, 10));

        for (let i = 0; i < 4; i++) {
            soma += numeros[i] * pesos[i];
        }

        const resto = soma % 11;

        if (resto === 0 || resto === 1) {
            digitoCalculado = '0'; // Resto 1 também vira 0 no Bradesco
        } else {
            const resultado = 11 - resto;
            if (resultado === 10) return false; // DV inválido para agência
            digitoCalculado = String(resultado);
        }
    } else {
        return false;
    }
    return digito.toUpperCase() === digitoCalculado;
};


